import { useState, useCallback, useEffect, useMemo } from 'react';
import { RowDragEvent } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PagesTable, { PagesTableRow } from '../../../components/PagesTable/PagesTable';
import useResponsive from '../../../hooks/useResponsive';
import useBrandLocations from '../../../hooks/useBrandLocations';
import { useBrandContext } from '../../../contexts/BrandContext';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { PAGES, applySavedPageOrder } from '../../../constants/PagesConstants';
import PAGE_VISIBILITY_HANDLERS from '../../../api/pageVisibility';
import { getPageOrder, updatePageOrder } from '../../../api/pageOrder';
import { editProfilePage } from '../../../api/profilePages';
import { isValidWebUrl } from '../../../utils/general';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/common/Button';
import { PAGE_ADD_PAGE, PAGE_CUSTOM_PAGE } from '../../../constants/UriConstants';
import { customPageKey, isCustomPage } from '../../../utils/customPages';

// Session cache of the hub's order + per-row hidden state, KEYED BY RESTAURANT so one restaurant's
// state never leaks into another (survives in-app navigation; resets on restaurant switch / reload).
// Reorder is never persisted server-side; backend-backed rows (Catering, Events) are re-synced from
// the server whenever the active restaurant changes.
let sessionCache: { restaurantID: number | null; rows: PagesTableRow[] } | null = null;

const defaultRows = (): PagesTableRow[] => PAGES.map((page) => ({ ...page, isHidden: false }));

const cachedRowsFor = (restaurantID: number | null): PagesTableRow[] =>
  sessionCache && sessionCache.restaurantID === restaurantID ? sessionCache.rows : defaultRows();

const CUSTOM_ROW_ID_OFFSET = 1000;

const withCustomRows = (rows: PagesTableRow[], customRows: PagesTableRow[]): PagesTableRow[] => {
  const byPageID = new Map(customRows.map((row) => [row.customPageID, row]));
  const kept = rows
    .filter((row) => !row.customPageID || byPageID.has(row.customPageID))
    .map((row) => (row.customPageID ? { ...byPageID.get(row.customPageID)!, isHidden: row.isHidden } : row));
  const keptIDs = new Set(kept.map((row) => row.customPageID));
  return [...kept, ...customRows.filter((row) => !keptIDs.has(row.customPageID))];
};

const PagesPage = () => {
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();
  const { currentRestaurant } = useRestaurantContext();
  const { currentBrand } = useBrandContext();
  const restaurantID = currentRestaurant?.restaurantID ?? null;
  // Empty until loaded, so multi-location rows appear once the brand's locations are known.
  const hasMultipleLocations = useBrandLocations().length > 1;
  const brandID = currentBrand?.id;
  const hasGiftCardsLink = isValidWebUrl(currentBrand?.giftCardsUrl);

  const [pages, setPages] = useState<PagesTableRow[]>(() => cachedRowsFor(restaurantID));

  const restaurantPages = currentRestaurant?.pages;
  const customRows = useMemo<PagesTableRow[]>(
    () =>
      (restaurantPages ?? []).filter(isCustomPage).map((page) => ({
        id: CUSTOM_ROW_ID_OFFSET + page.pageID,
        key: customPageKey(page.navLink),
        label: page.name,
        title: 'Custom page',
        route: `${PAGE_CUSTOM_PAGE}/${page.pageID}`,
        customPageID: page.pageID,
        isHidden: page.isHidden ?? false
      })),
    [restaurantPages]
  );

  // What the table shows: `pages` minus rows that don't apply to this brand, with brand-scoped
  // editor routes resolved. `pages` itself keeps every row so hidden ones keep their place.
  const shownPages = useMemo(
    () =>
      pages
        .filter((page) => !page.multiLocationOnly || hasMultipleLocations)
        .map((page) =>
          page.brandRoute ? { ...page, route: brandID ? `${brandID}/${page.brandRoute}` : undefined } : page
        )
        .map((page) =>
          page.requiresGiftCardsLink && !hasGiftCardsLink
            ? { ...page, isHidden: true, unavailableNote: 'Add a link to show' }
            : page
        ),
    [pages, hasMultipleLocations, brandID, hasGiftCardsLink]
  );

  // Persist the rendered rows to the restaurant-keyed session cache.
  const remember = useCallback(
    (rows: PagesTableRow[]) => {
      sessionCache = { restaurantID, rows };
    },
    [restaurantID]
  );

  // Reset to the active restaurant's rows and pull live visibility for backend-backed pages whenever
  // the active restaurant changes (the dropdown only updates context — PagesPage does not remount).
  useEffect(() => {
    let canceled = false;
    const base = cachedRowsFor(restaurantID);
    remember(base);
    setPages(base);
    if (restaurantID == null) return undefined;

    // Saved display order (best-effort — a failure just leaves the default order in place).
    const orderPromise = getPageOrder().catch(() => null);

    const visibilityPromise = Promise.all(
      PAGES.map(async (page) => {
        const key = page.visibility;
        if (!key) return null;
        try {
          const visible = await PAGE_VISIBILITY_HANDLERS[key].getVisible();
          return { id: page.id, isHidden: !visible, statusUnknown: false };
        } catch {
          // Couldn't read the real state — flag it so the row isn't silently presented as "Showing".
          return { id: page.id, statusUnknown: true };
        }
      })
    );

    Promise.all([orderPromise, visibilityPromise]).then(([savedOrder, results]) => {
      if (canceled) return;
      const byId = new Map(results.filter(Boolean).map((result) => [result!.id, result!]));
      const failedLabels = PAGES.filter((page) => byId.get(page.id)?.statusUnknown).map((page) => page.label);
      setPages((prev) => {
        let next = prev.map((page) => {
          const result = byId.get(page.id);
          if (!result) return page;
          if (result.statusUnknown) return { ...page, statusUnknown: true };
          return { ...page, isHidden: result.isHidden, statusUnknown: false };
        });
        if (savedOrder) {
          next = applySavedPageOrder(next, savedOrder);
        }
        remember(next);
        return next;
      });
      if (failedLabels.length) {
        toast.error(
          `Couldn't load current visibility for: ${failedLabels.join(', ')}. The status shown may be out of date.`,
          { position: toast.POSITION.TOP_RIGHT }
        );
      }
    });
    return () => {
      canceled = true;
    };
  }, [restaurantID, remember]);

  useEffect(() => {
    setPages((prev) => {
      const next = withCustomRows(prev, customRows);
      remember(next);
      return next;
    });
  }, [customRows, remember]);

  const handleHideShow = useCallback(
    (id: number) => {
      const page = pages.find((item) => item.id === id);
      if (!page) return;

      // Flip a single row functionally so concurrent toggles never clobber each other. Toggling
      // asserts a definite state, so any prior "status unavailable" flag is cleared.
      const setRowHidden = (hidden: boolean) =>
        setPages((prev) => {
          const next = prev.map((item) =>
            item.id === id ? { ...item, isHidden: hidden, statusUnknown: false } : item
          );
          remember(next);
          return next;
        });

      if (page.customPageID) {
        const nextHidden = !page.isHidden;
        setRowHidden(nextHidden);
        editProfilePage({ pageID: page.customPageID, isHidden: nextHidden }).catch(() => {
          setRowHidden(!nextHidden);
          toast.error(`Could not update ${page.label} visibility.`, { position: toast.POSITION.TOP_RIGHT });
        });
        return;
      }

      if (!page.visibility) {
        // Placeholder pages (e.g. Press) toggle the UI only — no backend yet. Pages with neither
        // visibility nor localHide leave Hide as a deliberate no-op.
        if (page.localHide) setRowHidden(!page.isHidden);
        return;
      }

      const nextHidden = !page.isHidden;
      setRowHidden(nextHidden); // optimistic
      PAGE_VISIBILITY_HANDLERS[page.visibility].setVisible(!nextHidden).catch(() => {
        setRowHidden(!nextHidden); // roll back just this row
        toast.error(`Could not update ${page.label} visibility.`, { position: toast.POSITION.TOP_RIGHT });
      });
    },
    [pages, remember]
  );

  const handleDragEnd = useCallback(
    (event: RowDragEvent) => {
      // Read the new visual order back from the grid, apply optimistically, then persist. Rows the
      // table isn't showing (e.g. Locations for a single-location brand) keep their slot, and the
      // shown rows fill the other slots in their new order.
      const reordered: PagesTableRow[] = [];
      event.api.forEachNode((node) => reordered.push(node.data));
      const shownIDs = new Set(reordered.map((row) => row.id));
      let nextShown = 0;
      const next = pages.map((row) => (shownIDs.has(row.id) ? reordered[nextShown++] : row));
      remember(next);
      setPages(next);
      updatePageOrder(next.map((row) => row.key)).catch(() => {
        toast.error('Could not save the new page order. Please try again.', { position: toast.POSITION.TOP_RIGHT });
      });
    },
    [pages, remember]
  );

  return (
    <div className="pages-page-container">
      <PageHeader title="PAGES">
        <Button className="page-header__action-button" onClick={() => navigate(`/${PAGE_ADD_PAGE}`)}>
          ADD PAGE
        </Button>
      </PageHeader>
      <p className="pages-page-hint">Drag to reorder how these pages appear on your website.</p>
      <PagesTable pages={shownPages} isDesktop={isDesktop} onHideShow={handleHideShow} onDragEnd={handleDragEnd} />
    </div>
  );
};

export default PagesPage;
